// ──────────────────────────────────────────────────────────────────────────────
// REFERRAL DEFAULTS
//
// These are DEFAULTS ONLY. They seed program v1 (see the referrals migration)
// and act as the fallback if the referral_program table is somehow empty. Every
// runtime read must go through getReferralProgram() in ./program — never import
// these to make a live decision, or admin edits silently stop mattering.
// ──────────────────────────────────────────────────────────────────────────────

import type {
  ReferralIssueMode,
  ReferralProgram,
  ReferralRewardablePurchases,
  ReferralTrigger,
} from "@/db/schema/referral";

/** ₹75 to the advocate. */
export const REFERRAL_DEFAULT_REFERRER_REWARD_MINOR = 7_500;

/** ₹75 to the friend. */
export const REFERRAL_DEFAULT_INVITEE_REWARD_MINOR = 7_500;

/**
 * The friend earns for finishing onboarding — instant gratification, and it
 * lifts onboarding completion. The advocate earns only once that friend
 * actually transacts, which caps the cost of a manufactured account at one
 * side's reward instead of two.
 */
export const REFERRAL_DEFAULT_INVITEE_TRIGGER: ReferralTrigger =
  "onboarding_complete";
export const REFERRAL_DEFAULT_REFERRER_TRIGGER: ReferralTrigger =
  "first_booking";

/** Any confirmed transaction counts, including a free booking. */
export const REFERRAL_DEFAULT_MIN_QUALIFYING_AMOUNT_MINOR = 0;

export const REFERRAL_DEFAULT_REWARDABLE_PURCHASES: ReferralRewardablePurchases =
  "first";
export const REFERRAL_DEFAULT_REWARDABLE_COUNT = 1;

/** 10 successful referrals a month → a ₹750/advocate/month ceiling. */
export const REFERRAL_DEFAULT_MONTHLY_SUCCESS_CAP: number | null = 10;

/** No lifetime ceiling — genuine advocates should keep earning. */
export const REFERRAL_DEFAULT_LIFETIME_SUCCESS_CAP: number | null = null;

/** Click → signup. The ecommerce standard. */
export const REFERRAL_DEFAULT_ATTRIBUTION_WINDOW_DAYS = 30;

/** How long a claimable reward waits before it lapses as breakage. */
export const REFERRAL_DEFAULT_CLAIM_WINDOW_DAYS = 30;

/**
 * NULL = the claimed lot never expires, matching CREDIT_DEFAULT_EXPIRY_DAYS.
 * Money that vanishes after being claimed is the version users complain about.
 */
export const REFERRAL_DEFAULT_CLAIMED_CREDIT_EXPIRY_DAYS: number | null = null;

export const REFERRAL_DEFAULT_ISSUE_MODE: ReferralIssueMode = "instant";
export const REFERRAL_DEFAULT_HOLD_PERIOD_DAYS = 0;

export const REFERRAL_DEFAULT_PER_DEVICE_ATTRIBUTION_CAP = 2;
export const REFERRAL_DEFAULT_MAX_ACCOUNT_AGE_DAYS = 30;

/**
 * Off until prod SMS OTP is wired — `sendOTP` in src/lib/auth.ts throws in
 * production today, so requiring a verified phone would block every referral.
 */
export const REFERRAL_DEFAULT_REQUIRE_PHONE_VERIFIED = false;

/** Lead time for the "your reward is about to expire" nudge. */
export const REFERRAL_CLAIM_EXPIRING_SOON_DAYS = 3;

/** Cookie that carries a code from a link/QR tap into account creation. */
export const PENDING_REFERRAL_COOKIE = "hiive_ref";

/** Companion cookie recording how the code arrived (link | qr | share). */
export const PENDING_REFERRAL_SOURCE_COOKIE = "hiive_ref_src";

/**
 * Code alphabet with the ambiguous glyphs removed (no O/0, no I/1/L) so a code
 * read aloud or off a screen at a venue cannot be mistyped.
 */
export const REFERRAL_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const REFERRAL_CODE_RANDOM_LENGTH = 6;
export const REFERRAL_CODE_MIN_LENGTH = 4;
export const REFERRAL_CODE_MAX_LENGTH = 16;

/**
 * The in-memory program used only when referral_program has no rows — a
 * belt-and-braces fallback so a missing seed degrades to "the documented
 * defaults" rather than a crash on the wallet page.
 */
export const REFERRAL_FALLBACK_PROGRAM: ReferralProgram = {
  id: 0,
  isPaused: false,
  validFrom: null,
  validUntil: null,
  referrerRewardMinor: REFERRAL_DEFAULT_REFERRER_REWARD_MINOR,
  inviteeRewardMinor: REFERRAL_DEFAULT_INVITEE_REWARD_MINOR,
  currency: "INR",
  inviteeTrigger: REFERRAL_DEFAULT_INVITEE_TRIGGER,
  referrerTrigger: REFERRAL_DEFAULT_REFERRER_TRIGGER,
  minQualifyingAmountMinor: REFERRAL_DEFAULT_MIN_QUALIFYING_AMOUNT_MINOR,
  rewardablePurchases: REFERRAL_DEFAULT_REWARDABLE_PURCHASES,
  rewardableCount: REFERRAL_DEFAULT_REWARDABLE_COUNT,
  monthlySuccessCap: REFERRAL_DEFAULT_MONTHLY_SUCCESS_CAP,
  lifetimeSuccessCap: REFERRAL_DEFAULT_LIFETIME_SUCCESS_CAP,
  attributionWindowDays: REFERRAL_DEFAULT_ATTRIBUTION_WINDOW_DAYS,
  claimWindowDays: REFERRAL_DEFAULT_CLAIM_WINDOW_DAYS,
  claimedCreditExpiryDays: REFERRAL_DEFAULT_CLAIMED_CREDIT_EXPIRY_DAYS,
  issueMode: REFERRAL_DEFAULT_ISSUE_MODE,
  holdPeriodDays: REFERRAL_DEFAULT_HOLD_PERIOD_DAYS,
  perDeviceAttributionCap: REFERRAL_DEFAULT_PER_DEVICE_ATTRIBUTION_CAP,
  maxAccountAgeDays: REFERRAL_DEFAULT_MAX_ACCOUNT_AGE_DAYS,
  requirePhoneVerified: REFERRAL_DEFAULT_REQUIRE_PHONE_VERIFIED,
  updatedByUserId: null,
  note: "In-memory fallback — referral_program has no rows.",
  createdAt: new Date(0),
  updatedAt: new Date(0),
};
